/**
 * Rename a clip's display name, from the pencil beside it in the Library and in Tagging's Browse tab.
 * The stored file keeps its original name for duplicate detection.
 */
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { queryClient, trpc } from '@/lib/trpc';

const CLIP_NAME_MAX = 120;

export function RenameClipModal({ assetId, name, onClose }: { assetId: string; name: string; onClose: () => void }) {
  const [text, setText] = useState(name);
  const rename = useMutation(trpc.assets.rename.mutationOptions({
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: trpc.library.pathKey() }),
        queryClient.invalidateQueries({ queryKey: trpc.tags.pathKey() }),
        queryClient.invalidateQueries({ queryKey: trpc.assets.pathKey() }),
      ]);
      onClose();
    },
  }));
  const submit = () => {
    if (text.trim()) rename.mutate({ assetId, name: text.trim() });
  };
  return (
    <>
      <button type="button" className="overlay overlay-btn" aria-label="Close" onClick={onClose} />
      <div className="modal managed-modal" role="dialog" aria-label="Rename clip">
        <h2>Rename clip</h2>
        <label className="field">
          <input autoFocus value={text} aria-label="Display name" maxLength={CLIP_NAME_MAX}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit();
              if (e.key === 'Escape') onClose();
            }} />
        </label>
        <span className="sub">The display name only; the stored file keeps its original name for duplicate detection.</span>
        <div className="row between">
          <button type="button" className="btn quiet" onClick={onClose}>Cancel</button>
          <button type="button" className="btn primary" disabled={rename.isPending || !text.trim()} onClick={submit}>
            {rename.isPending ? 'Saving…' : 'Rename'}
          </button>
        </div>
        {rename.isError && <div className="error" role="alert">{rename.error.message}</div>}
      </div>
    </>
  );
}
